<template>
  <div class="search-resource-media-detail">
    <inline-loader v-if="isLoading" />
    <template v-else>
      <div class="form-group">
        <cv-form class="meta">
          <cv-inline-notification
            v-if="showInlineNotification"
            :title="notification.title"
            low-contrast
            class="banner"
            kind="info"
            hide-close-button
          >
            <template #subtitle>
              <div v-safe-html="notification.subtitle" />
            </template>
          </cv-inline-notification>

          <!-- location -->
          <dl v-if="!isAdminView" class="field-set type-text location">
            <dt class="field-label">{{ $t("search_resource_detail_location_label") }}</dt>
            <dd class="field-value">
              <span class="message">
                {{ params.location }}
              </span>
            </dd>
          </dl>
          <!-- Status -->
          <dl v-if="isAdminView" class="field-set type-text status">
            <dt class="field-label">{{ $t("search_resource_detail_status_label") }}</dt>
            <dd class="field-value status-label">
              <label-component
                kind="resource-status"
                :text="displayStatus(params.status)"
                :class="statusTagClass(params.status)"
              />
            </dd>
            <dd class="field-value status-history">
              <link-button class="action" @click="handleViewStatusHistory">
                {{ $t("search_resource_detail_status_history_label") }}
              </link-button>
            </dd>
          </dl>
        </cv-form>

        <cv-form class="other-information">
          <h5 class="form-header">{{ $t("search_resource_detail_general_information_label") }}</h5>
          <!-- location -->
          <dl v-if="isAdminView" class="field-set type-text location">
            <dt class="field-label">{{ $t("search_resource_detail_location_label") }}</dt>
            <dd class="field-value">
              <span class="message">
                {{ getResource.resourceDomain }}
              </span>
            </dd>
          </dl>
          <!-- File Size -->
          <dl class="field-set type-text file-size">
            <dt class="field-label">{{ $t("search_resource_detail_file_size_label") }}</dt>
            <dd class="field-value">
              <template v-if="fileSize">
                <span>
                  {{ fileSize }}
                </span>
              </template>
              <span v-else class="message">-</span>
            </dd>
          </dl>
          <!-- created by -->
          <dl v-if="createdBy" class="field-set type-text created-by">
            <dt class="field-label">{{ $t("search_resource_detail_created_by_label") }}</dt>
            <dd class="field-value">
              <label-component
                kind="creditor"
                :class="{ 'creditor-moe': !canOpenProfile }"
                :text="createdBy"
                @click="handleProfileClick(createdById, isMOEResource)"
              />
              <time class="date">{{ createdDate }}</time>
            </dd>
          </dl>
          <!-- Modified by -->
          <dl class="field-set type-text modified-by">
            <dt class="field-label">{{ $t("search_resource_detail_edited_label") }}</dt>
            <dd class="field-value">
              <label-component
                kind="creditor"
                :text="modifiedBy"
                @click="handleProfileClick(modifiedById, false)"
              />
              <time class="date">{{ modifiedDate }}</time>
            </dd>
          </dl>
          <!-- keywords -->
          <match-indicator v-if="matchKeyword" />
          <dl class="field-set type-text keywords">
            <dt class="field-label">{{ $t("search_resource_detail_keywords_label") }}</dt>
            <dd class="field-value">
              <div v-if="showKeywordsTags" class="output-text">
                <keywords :tags="keywords" />
              </div>
              <div v-else class="output-text">
                {{ keywords }}
              </div>
              <div class="truncate" v-if="showTruncate(0)" />
            </dd>
          </dl>
        </cv-form>

        <cv-form class="copyright-period-access">
          <h5 class="form-header">
            {{ $t("search_resource_detail_copyright_period_and_access_label") }}
          </h5>
          <!-- start date -->
          <dl class="field-set type-text modified-by">
            <dt class="field-label">{{ $t("search_resource_detail_start_date_label") }}</dt>
            <dd class="field-value">
              <span class="date">{{ startDate }}</span>
            </dd>
          </dl>
          <!-- end date -->
          <dl class="field-set type-text modified-by">
            <dt class="field-label">{{ $t("search_resource_detail_end_date_label") }}</dt>
            <dd class="field-value">
              <span class="date">{{ validity }}</span>
            </dd>
          </dl>

          <!--License Duration -->
          <dl v-if="showLicenseDuration" class="field-set type-text copyright-duration">
            <dt class="field-label">{{ $t("search_resource_detail_license_duration_label") }}</dt>
            <dd class="field-value">
              <span class="duration-year">{{
                $t("search_resource_detail_duration_years_label", { year: licenseDuration.years })
              }}</span>
              <span class="duration-month">{{
                $t("search_resource_detail_duration_months_label", {
                  month: licenseDuration.months,
                })
              }}</span>
              <span class="duration-day">{{
                $t("search_resource_detail_duration_days_label", { day: licenseDuration.days })
              }}</span>
              <p class="duration-caption">
                {{ $t("search_resource_detail_duration_caption_label") }}
              </p>
            </dd>
          </dl>

          <!-- availability -->
          <template v-if="isMediaType && !isAdminView">
            <dl class="field-set type-text availability">
              <dt class="field-label">{{ $t("search_resource_detail_availability_label") }}</dt>
              <dd class="field-value">
                <span class="message">{{ availability }}</span>
              </dd>
            </dl>
            <!-- Subject -->
            <dl class="field-set type-text subject" v-if="isRestricted">
              <dt class="field-label">{{ $t("search_resource_detail_subject_label") }}</dt>
              <dd class="field-value">
                <span class="message">{{ subjectList }}</span>
              </dd>
            </dl>
            <!-- Level -->
            <dl class="field-set type-text level" v-if="isRestricted">
              <dt class="field-label">{{ $t("search_resource_detail_level_label") }}</dt>
              <dd class="field-value">
                <span class="message">{{ levelList }}</span>
              </dd>
            </dl>
          </template>
        </cv-form>

        <cv-form v-if="isAdminView" class="copyright-details">
          <h5 class="form-header">{{ $t("search_resource_detail_copyright_details_label") }}</h5>
          <!-- Owner -->
          <dl class="field-set type-text owner">
            <dt class="field-label">{{ $t("search_resource_detail_owner_label") }}</dt>
            <dd class="field-value">
              <span>{{ owner }}</span>
            </dd>
          </dl>
          <!-- IP S/N -->
          <dl class="field-set type-text ip-sn">
            <dt class="field-label">{{ $t("search_resource_detail_ip_repository_sn_label") }}</dt>
            <dd class="field-value">
              <span>{{ getIPSN }}</span>
            </dd>
          </dl>
          <!-- Perpetual License -->
          <dl class="field-set type-text perpetual-license">
            <dt class="field-label">{{ $t("search_resource_detail_perpetual_license_label") }}</dt>
            <dd class="field-value">
              <span>{{ getBooleanDisplay(copyrightData.perpetual) }}</span>
            </dd>
          </dl>
          <!-- credit -->
          <dl class="field-set type-text credit">
            <dt class="field-label">{{ $t("search_resource_detail_credit_label") }}</dt>
            <dd class="field-value">
              <span>{{ copyrightData.credit }}</span>
            </dd>
          </dl>
          <!-- Single use license -->
          <dl class="field-set type-text single-use-license">
            <dt class="field-label">{{ $t("search_resource_detail_single_use_license_label") }}</dt>
            <dd class="field-value">
              <span>{{ getBooleanDisplay(copyrightData.singleUseLicense) }}</span>
            </dd>
          </dl>
          <!-- License Territory -->
          <dl class="field-set type-text license-territory">
            <dt class="field-label">{{ $t("search_resource_detail_license_territory_label") }}</dt>
            <dd class="field-value">
              <span>{{ copyrightData.territoryUI }}</span>
            </dd>
          </dl>
          <!-- License Term -->
          <dl class="field-set type-text license-territory">
            <dt class="field-label">{{ $t("search_resource_detail_license_terms_label") }}</dt>
            <dd class="field-value">
              <span>{{ copyrightData.otherTerms }}</span>
            </dd>
          </dl>
        </cv-form>

        <cv-form v-if="isAdminView" class="permission-details">
          <h5 class="form-header">{{ $t("search_resource_detail_permission_details_label") }}</h5>
          <!-- Owner Group -->
          <dl class="field-set type-text owner-group">
            <dt class="field-label">{{ $t("search_resource_detail_owner_group_label") }}</dt>
            <dd class="field-value">
              <span v-if="ownerGroup" class="message">
                {{ ownerGroup }}
              </span>
              <span v-else class="message">-</span>
            </dd>
          </dl>
          <!-- Group Access -->
          <dl class="field-set type-text availability">
            <dt class="field-label">{{ $t("search_resource_detail_group_access_label") }}</dt>
            <dd class="field-value">
              <span class="message">
                {{ availability }}
              </span>
            </dd>
          </dl>
          <!-- Subject -->
          <dl class="field-set type-text subject" v-if="isRestricted">
            <dt class="field-label">{{ $t("search_resource_detail_subject_label") }}</dt>
            <dd class="field-value">
              <span class="message">{{ subjectList }}</span>
            </dd>
          </dl>

          <!-- Level -->
          <dl class="field-set type-text level" v-if="isRestricted">
            <dt class="field-label">{{ $t("search_resource_detail_level_label") }}</dt>
            <dd class="field-value">
              <span class="message">{{ levelList }}</span>
            </dd>
          </dl>
          <!-- Searchable by teacher -->
          <dl class="field-set type-text searchable">
            <dt class="field-label">{{ $t("search_resource_detail_searchable_label") }}</dt>
            <dd class="field-value">
              <span>{{ getBooleanDisplay(getResource.accessibleByTeacher) }}</span>
            </dd>
          </dl>
          <!-- Downloadable -->
          <dl class="field-set type-text downloadable">
            <dt class="field-label">{{ $t("search_resource_detail_downloadable_label") }}</dt>
            <dd class="field-value">
              <span>{{ getBooleanDisplay(copyrightData.downloadable) }}</span>
            </dd>
          </dl>
          <!-- Printable -->
          <dl class="field-set type-text printable">
            <dt class="field-label">{{ $t("search_resource_detail_printable_label") }}</dt>
            <dd class="field-value">
              <span>{{ getBooleanDisplay(copyrightData.printable) }}</span>
            </dd>
          </dl>
          <!-- Modification -->
          <dl class="field-set type-text modification">
            <dt class="field-label">{{ $t("search_resource_detail_modification_label") }}</dt>
            <dd class="field-value">
              <span>{{ modification }}</span>
            </dd>
          </dl>

          <!-- Remarks -->
          <dl class="field-set type-text remarks">
            <dt class="field-label">{{ $t("search_resource_detail_remarks_label") }}</dt>
            <dd class="field-value">
              <rich-text
                v-if="copyrightData.remarks"
                class="description"
                :content="copyrightData.remarks"
                truncationType="secondary"
              />
              <span v-else class="message">-</span>
            </dd>
          </dl>
        </cv-form>

        <cv-form class="linked-resources">
          <div v-if="!isAdminView" class="field-set type-text used-in">
            <AccordionWrapper
              kind="ghost"
              class="accordion-component"
              @state-change="accordionActionChange"
            >
              <cv-accordion-item key="usedIn" :open="false">
                <template #title>{{ usedInLabel }} </template>
                <template #content>
                  <div v-if="hasContextData" class="lesson-card-wrapper">
                    <lesson-detail-card
                      v-for="data in contextData"
                      :key="data.uuid"
                      :contextData="data"
                      :searchFilters="params.searchFilters"
                    />
                  </div>
                  <span class="message" v-else>-</span>
                </template>
              </cv-accordion-item>
            </AccordionWrapper>
          </div>
          <!-- Used In -->
          <dl v-else class="field-set type-text used-in">
            <!-- Used In -->
            <dl class="field-set type-text used-in">
              <dt class="field-label">{{ $t("search_resource_detail_used_in_label") }}</dt>
              <dd class="field-value">
                <linked-resources-table-list :resourceUuid="getResource.uuid" />
              </dd>
            </dl>
          </dl>
        </cv-form>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import SettingsSummaryMixin from "@modules/editor/mixin/settings.summary.mixin";
import Keywords from "@plugins/keywords/components/Keywords";
import DateTimeFormatter from "@utils/datetime.formatter";
import LessonDetailCard from "@plugins/common/components/LessonDetailCard";
import resourceApi from "@apis/resource.api";
import mediaObjectApi from "@apis/media.object.api";
import { isEmpty } from "@utils/var.check";
import { LOCATION_TYPE, NAMESPACE, RESOURCE_DOMAIN } from "@modules/search/service/search.constant";
import DateTimeUtils from "@utils/datetime.utils";
import LinkedResourcesTableList from "@modules/search/components/LinkedResourcesTableList";
import StatusHistoryContentSubpage from "@plugins/status-history/components/StatusHistoryContentSubpage";
import ActivityIconMixin from "@plugins/common/mixin/activity.icon.mixin";

import AccordionComponentStore from "@plugins/accordion/components/AccordionComponentStore";
import RoutingMixin from "@plugins/common/mixin/routing.mixin";
import { AVAILABILITY, LICENSE_TYPE } from "@plugins/media/media.constant";
import { mapGetters } from "vuex";
import SearchProfile from "@modules/search/mixin/search.profile.mixin.ts";

export default {
  name: "SearchMediaResourceDetail",
  mixins: [SettingsSummaryMixin, ActivityIconMixin, RoutingMixin, SearchProfile],
  components: { LinkedResourcesTableList, LessonDetailCard, Keywords },
  props: { params: { type: Object, default: null }, context: { type: String, required: true } },
  data() {
    return {
      activityTitle: `Resource`,
      visible: true,
      hasPlaceholder: true,
      isLoading: true,
      contextData: {},
      notification: {
        title: this.$i18n.t("search_resource_detail_notification_title"),
        subtitle: this.$i18n.t("search_resource_detail_notification_subtitle", {
          resource: this.params.type.charAt(0) + this.params.type.slice(1).toLowerCase(),
        }),
      },
      showInlineNotification: this.params.type === "QUESTION" || this.params.type === "QUIZ",
      selectAllActivities: [],
      accordionStore: AccordionComponentStore,
      licenseDuration: {},
    };
  },
  computed: {
    ...mapGetters(NAMESPACE.AUTH, ["isCA", "isCO"]),
    isMediaType() {
      return this.params.type === "MEDIA";
    },
    mediaMetadata() {
      return this.params.mediaObject;
    },
    copyrightData() {
      return this.params.copyright;
    },
    getResource() {
      return this.mediaMetadata;
    },
    title() {
      return this.getResource.title;
    },
    description() {
      return this.getResource.description;
    },
    contributors() {
      if (
        this.getResource.resourceDomain === LOCATION_TYPE[2].value ||
        this.getResource.publisher === LOCATION_TYPE[2].value
      ) {
        return this.isCA || this.isCO
          ? this.$i18n.t("search_resource_detail_publisher_label") + ` ${this.ownerGroup}`
          : this.$i18n.t("search_resource_detail_publisher_label");
      } else {
        const originator = this.getResource.originator;
        const originatorName = this.getResource.originatorName;
        if (isDefined(originator)) {
          return originator.name;
        } else if (originatorName) {
          return originatorName;
        }
        return "-";
      }
    },
    canOpenProfile() {
      return !this.isMOEResource || this.createdById !== 0;
    },
    isAdminView() {
      return this.params.admin && this.params.admin === "admin";
    },
    fileDuration() {
      return !isEmpty(this.getResource.fileDurationFriendly)
        ? this.getResource.fileDurationFriendly
        : "-";
    },
    keywords() {
      return this.getResource.keywords ? this.getResource.keywords : "-";
    },
    licenseType() {
      return this.getResource?.licenseType ?? "";
    },
    showLicenseDuration() {
      return this.licenseType === LICENSE_TYPE.TERM.value && !isEmpty(this.licenseDuration);
    },
    validity() {
      if (this.licenseType === LICENSE_TYPE.PERPETUAL.value) {
        return "Perpetual";
      }
      if (!this.isAdminView) {
        return !isEmpty(this.getResource?.endDateUI) ? this.getResource?.endDateUI : "-";
      }
      return !isEmpty(this.copyrightData?.endDateUI) ? this.copyrightData?.endDateUI : "-";
    },
    startDate() {
      if (!this.isAdminView) {
        return !isEmpty(this.getResource.startDateUI) ? this.getResource.startDateUI : "-";
      }

      return this.copyrightData && this.copyrightData.startDateUI
        ? this.copyrightData.startDateUI
        : "-";
    },
    isRestricted() {
      return this.getResource.availability === AVAILABILITY.RESTRICTED.value;
    },
    availability() {
      const availability = this.getResource?.availability ?? "";
      if (notNull(AVAILABILITY[availability]?.label)) {
        return this.$t(AVAILABILITY[availability].label);
      } else {
        return "-";
      }
    },
    subjectList() {
      const subjectList =
        this.getResource?.subjects?.map((item) => item.subjectName).join(", ") ?? "";
      return isEmpty(subjectList) ? "-" : subjectList;
    },
    levelList() {
      const levelList =
        this.getResource?.levelAndStreams?.map((item) => item.displayName).join(", ") ?? "";
      return isEmpty(levelList) ? "-" : levelList;
    },
    createdDate() {
      return DateTimeFormatter.formatDateTimeToDateTimeAmPm(this.getResource.createdDate);
    },
    modifiedDate() {
      return DateTimeFormatter.formatDateTimeToDateTimeAmPm(this.getResource.modifiedDate);
    },
    getDate() {
      return (date) => {
        return date ? DateTimeFormatter.formatLocalDateTimeToDateOnly(date) : "-";
      };
    },
    modifiedBy() {
      return this.mediaMetadata.modifiedByName;
    },
    createdBy() {
      return this.getResource.resourceDomain === RESOURCE_DOMAIN.MOE
        ? this.isCA || this.isCO
          ? this.$i18n.t("search_resource_detail_publisher_label") + ` ${this.ownerGroup}`
          : this.$i18n.t("search_resource_detail_publisher_label")
        : this.getResource.createdByName;
    },
    ownerGroup() {
      return this.getResource.ownerGroup;
    },
    owner() {
      return this.copyrightData ? this.copyrightData.ownerUI : "-";
    },
    modification() {
      if (!this.copyrightData) {
        return "-";
      }
      return this.copyrightData.singleUseLicense
        ? this.$i18n.t("no_label")
        : this.getBooleanDisplay(this.copyrightData.modification);
    },
    getBooleanDisplay() {
      return (value) => {
        return value ? this.$i18n.t("yes_label") : this.$i18n.t("no_label");
      };
    },
    getIPSN() {
      return this.copyrightData && this.copyrightData.filingReference
        ? this.copyrightData.filingReference
        : "-";
    },
    showKeywordsTags() {
      return this.getResource.keywords && this.getResource.keywords.length > 0;
    },
    hasContextData() {
      return !isEmpty(this.contextData);
    },
    fileSize() {
      return this.mediaMetadata.fileSizeFriendly;
    },
    statusTagClass() {
      return (status) => {
        return status?.trim().replaceAll("_", "-").toLowerCase();
      };
    },
    displayStatus() {
      return (status) => {
        return status?.replaceAll("_", " ");
      };
    },
    usedInLabel() {
      const length = this.hasContextData ? this.contextData.length : 0;
      return `${this.$i18n.t("search_resource_detail_used_in_label")} (${length})`;
    },
    matchKeyword() {
      return isEmpty(this.params.highlights) ? null : this.params.highlights.keyword;
    },
    isMOEResource() {
      return this.getResource.resourceDomain === RESOURCE_DOMAIN.MOE;
    },
    createdById() {
      return this.getResource.createdById;
    },
    modifiedById() {
      return this.getResource.modifiedById;
    },
  },
  methods: {
    accordionActionChange(data, row) {
      const accordionIndex = data.changedIndex;
      const accordionState = data.state[accordionIndex];
      const accordionItemId = row?.columns[accordionIndex].componentId;
      this.accordionStore.setAccordionItemState(row?.componentId, accordionItemId, accordionState);
    },
    calculateDuration(start, end) {
      if (!start || !end) {
        this.licenseDuration = { years: 0, months: 0, days: 0 };
        return;
      }
      const formartStartDate = DateTimeUtils.parseLocalDateTime(start);
      const formartEndDate = DateTimeUtils.parseLocalDateTime(end);

      //Get the year of this year
      let thisFullYear = formartEndDate.getFullYear();
      //Check how many days there where in last month
      const daysInLastMonth = new Date(thisFullYear, formartEndDate.getMonth(), 0).getDate();
      //Get this month
      let thisMonth = formartEndDate.getMonth();
      //Subtract to get difference between years
      thisFullYear = thisFullYear - formartStartDate.getFullYear();
      //Subtract to get difference between months
      thisMonth = thisMonth - formartStartDate.getMonth();
      //If month is less than 0 it means that we are some months before the start date in the year.
      // So we subtract one year, and add the negative number (month) to 12. (12 + -1 = 11)
      const thisDay = formartEndDate.getDate();
      thisMonth = thisMonth - 1;
      if (thisMonth < 0) {
        thisFullYear = thisFullYear - 1;
        thisMonth = 12 + thisMonth;
        //Get ends day of the month
      }
      //Subtract the start date from the number of days in the last month, add the result to todays day of the month
      //From 1-Aug-2016 to 1-Sep-2016 is to be considered as 1 month and 1 day
      let subAddDays = daysInLastMonth - formartStartDate.getDate() + 1;
      subAddDays = thisDay + subAddDays;

      if (subAddDays >= daysInLastMonth) {
        subAddDays = subAddDays - daysInLastMonth;
        thisMonth++;
        if (thisMonth > 11) {
          thisFullYear++;
          thisMonth = 0;
        }
      }

      this.licenseDuration = { years: thisFullYear, months: thisMonth, days: subAddDays };
    },
    handleViewStatusHistory() {
      this.$contentSubpage.show(StatusHistoryContentSubpage, {
        baseTitle: [this.getResource.title],
        resourceUuid: this.params.uuid,
        resourceDomain: "MOE",
        resourceStatus: this.params.status,
        resourceType: this.params.type,
        hasParentSubpage: true,
      });
    },
    handleDownload() {
      const uuid = this.params.uuid;
      mediaObjectApi.downloadMediaObject({ uuid: uuid }).then((response) => {
        //OK
        if (response.code === "ok") {
          this.target = "DOWNLOAD_MEDIA_OBJECT";
          this.urlReady = response.data;
        } else {
          //todo: error message
        }
      }, this.passiveApiFailHandler); //OK
    },
  },
  created() {
    if (this.isAdminView) {
      if (this.copyrightData?.licenseType === LICENSE_TYPE.TERM.value) {
        this.calculateDuration(this.copyrightData.startDate, this.copyrightData.endDate);
      }
      this.isLoading = false;
      return;
    } else {
      if (this.getResource?.licenseType === LICENSE_TYPE.TERM.value) {
        this.calculateDuration(this.getResource.startDate, this.getResource.endDate);
      }
    }

    if (this.isAdminView) {
      this.isLoading = false;
      return;
    }
    const data = {
      uuid: this.params.uuid,
      type: this.params.type === "MEDIA" ? "MEDIAOBJECT" : this.params.type,
      raw: true, // get full context info
    };
    this.isLoading = true;
    resourceApi.fetchResourceContext(data).then((response) => {
      //OK
      if (response) {
        this.isLoading = false;
        this.contextData = response.data;
      }
    }, this.passiveApiFailHandler); //OK
  },
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-resource-media-detail {
  height: 100%;
  .form-group {
    padding: $spacing-05;
    position: relative;
  }
  .action-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    position: sticky;
    bottom: 0;
    min-height: 10%;
    margin: 0 $spacing-05;
    padding: $spacing-05;
    background-color: $ui-01;
    border-top: $divider-02;
    z-index: 300;
  }

  .bx--form {
    &:first-of-type {
      margin-top: 0;
    }

    &.other-information {
      .date {
        flex: 0 0 100%;
        margin-top: $spacing-03;
      }

      .field-value {
        display: flex;
        flex-flow: row wrap;
      }
    }
  }

  .form-header {
    margin-bottom: $spacing-05;
  }

  .copyright-duration {
    .field-value {
      > span {
        &:not(:last-of-type) {
          margin-right: $spacing-06;
        }
      }
    }
  }

  .field-set {
    &.file-size {
      .bx--link {
        text-decoration: none;
      }

      .sls-icon {
        margin-left: $spacing-02;
        [stroke] {
          stroke: $interactive-01;
        }
      }
    }

    &.status {
      display: flex;
      flex-flow: row wrap;
      align-items: center;

      .field-label {
        @include carbon--type-style("label-03", true);
        color: $text-01;
        text-transform: uppercase;
      }

      .status-label {
        margin: 0 0 0 $spacing-05;
      }

      .status-history {
        flex: 0 0 100%;
      }
    }
    &:not(.used-in) {
      .label-component {
        &:not(.resource-status) {
          span {
            @include carbon--type-style("label-01", true);
            color: $text-01;
          }
        }
      }
    }
  }
}
</style>
